import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useUiOverlay } from "@/contexts/UiOverlayContext";
import AssistantPanel from "@/components/AssistantPanel";
import RoboBallImage from "@/components/RoboBallImage";
import EyeExpression from "@/components/figutour/EyeExpression";
import { useFiguTour } from "@/components/figutour/FiguTourProvider";
import { useI18n } from "@/lib/I18nContext";

const STORAGE_KEY = "fm_roboball_position";
const FAB_MOBILE = 56;
const FAB_DESKTOP = 64;
const DRAG_THRESHOLD_PX = 5;
const VIEWPORT_MARGIN = 8;
const TOUR_TRANSITION_MS = 300;
const TOUR_TRANSITION = `left ${TOUR_TRANSITION_MS}ms ease, top ${TOUR_TRANSITION_MS}ms ease`;
const TOUR_GAP = 24;

function getFabSize() {
  if (typeof window === "undefined") return FAB_DESKTOP;
  return window.matchMedia?.("(min-width: 768px)").matches
    ? FAB_DESKTOP
    : FAB_MOBILE;
}

function clampToViewport(x, y, size) {
  if (typeof window === "undefined") return { x, y };
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  return {
    x: Math.max(VIEWPORT_MARGIN, Math.min(vw - size - VIEWPORT_MARGIN, x)),
    y: Math.max(VIEWPORT_MARGIN, Math.min(vh - size - VIEWPORT_MARGIN, y)),
  };
}

function computeDefaultPosition(size) {
  if (typeof window === "undefined") return { x: 0, y: 0 };
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const rightInset = 16;
  const bottomInset = window.matchMedia("(min-width: 768px)").matches
    ? 16
    : 80; // clears the mobile bottom-nav
  return { x: vw - size - rightInset, y: vh - size - bottomInset };
}

function readStoredPosition(size) {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed?.x !== "number" || typeof parsed?.y !== "number") {
      return null;
    }
    const clamped = clampToViewport(parsed.x, parsed.y, size);
    if (
      Math.abs(clamped.x - parsed.x) > 4 ||
      Math.abs(clamped.y - parsed.y) > 4
    ) {
      return null;
    }
    return clamped;
  } catch (_) {
    return null;
  }
}

/**
 * Compute the FAB's tour-target position based on the current step.
 * Returns null when the ball should stay put (target === "roboball").
 */
function computeTourPosition(step, size) {
  if (!step) return null;
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  // Welcome — center-bottom of viewport.
  if (step.target === null) {
    return clampToViewport(
      (vw - size) / 2,
      vh - size - 160,
      size
    );
  }

  // Sentinel: launch step keeps the ball where it is (spotlight surrounds it).
  if (step.target === "roboball") {
    return null;
  }

  // Locate the DOM target.
  const el = document.querySelector(step.target);
  if (!el) return null;
  const rect = el.getBoundingClientRect();

  // Try RIGHT of target.
  const rightX = rect.right + TOUR_GAP;
  const centerY = rect.top + rect.height / 2 - size / 2;
  if (rightX + size + VIEWPORT_MARGIN <= vw) {
    return clampToViewport(rightX, centerY, size);
  }
  // Try LEFT.
  const leftX = rect.left - size - TOUR_GAP;
  if (leftX >= VIEWPORT_MARGIN) {
    return clampToViewport(leftX, centerY, size);
  }
  // Fall BELOW.
  const belowX = rect.left + rect.width / 2 - size / 2;
  const belowY = rect.bottom + TOUR_GAP;
  return clampToViewport(belowX, belowY, size);
}

export default function RoboBallFab() {
  const [open, setOpen] = useState(false);
  const { hasOverlay } = useUiOverlay();
  const location = useLocation();
  const { isActive: tourActive, currentStep } = useFiguTour();
  const { isAsl, t } = useI18n();

  const [size, setSize] = useState(() => getFabSize());
  const [pos, setPos] = useState(() => {
    const s = getFabSize();
    return readStoredPosition(s) || computeDefaultPosition(s);
  });

  // Phase 19c/d — inner motion classes (`rolling`, `bouncing`).
  const [motion, setMotion] = useState("");
  const motionTimersRef = useRef({ roll: null, bounce: null });
  const fireRoll = useCallback(() => {
    if (motionTimersRef.current.roll) {
      clearTimeout(motionTimersRef.current.roll);
    }
    setMotion((prev) =>
      prev.includes("rolling") ? prev : `${prev} rolling`.trim()
    );
    motionTimersRef.current.roll = setTimeout(() => {
      setMotion((prev) =>
        prev
          .split(" ")
          .filter((c) => c && c !== "rolling")
          .join(" ")
      );
      motionTimersRef.current.roll = null;
    }, 620);
  }, []);
  const fireBounce = useCallback(() => {
    if (motionTimersRef.current.bounce) {
      clearTimeout(motionTimersRef.current.bounce);
    }
    setMotion((prev) =>
      prev.includes("bouncing") ? prev : `${prev} bouncing`.trim()
    );
    motionTimersRef.current.bounce = setTimeout(() => {
      setMotion((prev) =>
        prev
          .split(" ")
          .filter((c) => c && c !== "bouncing")
          .join(" ")
      );
      motionTimersRef.current.bounce = null;
    }, 420);
  }, []);

  // Track the position to restore when the tour ends. Set the moment the
  // tour becomes active; cleared when the tour ends AND the restore
  // transition has settled.
  const preTourPositionRef = useRef(null);
  const posRef = useRef(pos);
  const [smoothTransition, setSmoothTransition] = useState(false);

  // Drag internals — refs to avoid re-render churn during drag.
  const dragRef = useRef({
    startX: 0,
    startY: 0,
    origX: 0,
    origY: 0,
    moved: false,
    active: false,
    rafId: null,
    pending: null,
  });
  const [isDragging, setIsDragging] = useState(false);
  // Stable handler refs — captured on mount, always read latest deps via
  // dragRef + posRef. Prevents the useEffect-race that could swallow a
  // synthesised mouseup fired immediately after mousedown.
  const globalMoveRef = useRef(null);
  const globalUpRef = useRef(null);

  // Auto-close panel on route change.
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  // Phase 19c/d — fire roboRoll on top-level bottom-nav change (excludes
  // the initial mount by tracking previous pathname). Explicitly skipped
  // on `/app/admin` (quiet surface — no decorative motion there).
  const prevPathRef = useRef(location.pathname);
  useEffect(() => {
    const prev = prevPathRef.current;
    const next = location.pathname;
    prevPathRef.current = next;
    if (prev === next) return;
    // Only fire on top-level /app tab changes. Skip sub-routes like
    // /app/chat/:threadId → /app/chat (still same top-level tab) via a
    // string-normalised "primary segment" comparison.
    const primary = (p) => {
      // "/app" → "app", "/app/radar" → "radar", "/app/chat/xyz" → "chat"
      const parts = p.split("/").filter(Boolean);
      return parts.length >= 2 ? parts[1] : parts[0] || "";
    };
    if (primary(prev) === primary(next)) return;
    if (isAsl) return; // ASL motion-reduce sweep already collapses; skip.
    fireRoll();
  }, [location.pathname, fireRoll, isAsl]);

  // Clamp on window resize (also refresh mobile/desktop size).
  useEffect(() => {
    const onResize = () => {
      const nextSize = getFabSize();
      setSize(nextSize);
      setPos((p) => clampToViewport(p.x, p.y, nextSize));
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const persist = useCallback((x, y) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ x, y }));
    } catch (_) {
      /* ignore quota / disabled storage */
    }
  }, []);

  // ── Tour-mode positioning ───────────────────────────────────────────
  // When the tour activates, snapshot the current position; recompute
  // target position on step change; restore + settle on tour end.
  // Uses posRef (kept in sync via effect below) so pos changes don't
  // re-run this effect and cause a compute→setPos→compute loop.
  useEffect(() => {
    posRef.current = pos;
  }, [pos]);

  const stepId = currentStep?.id || null;
  useEffect(() => {
    if (tourActive) {
      if (preTourPositionRef.current == null) {
        preTourPositionRef.current = { x: posRef.current.x, y: posRef.current.y };
      }
      setSmoothTransition(true);
      const target = computeTourPosition(currentStep, size);
      if (target) {
        setPos((prev) =>
          prev.x === target.x && prev.y === target.y ? prev : target
        );
      }
      return undefined;
    }
    // Tour just ended (or never started this render).
    if (preTourPositionRef.current) {
      const restore = preTourPositionRef.current;
      preTourPositionRef.current = null;
      setSmoothTransition(true);
      setPos((prev) =>
        prev.x === restore.x && prev.y === restore.y ? prev : restore
      );
      const t = setTimeout(
        () => setSmoothTransition(false),
        TOUR_TRANSITION_MS + 50
      );
      return () => clearTimeout(t);
    }
    return undefined;
  }, [tourActive, stepId, size, currentStep]);

  // Recompute tour position on window resize while active.
  useEffect(() => {
    if (!tourActive) return undefined;
    const onResize = () => {
      const target = computeTourPosition(currentStep, size);
      if (target) setPos(target);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [tourActive, currentStep, size]);

  // ── Drag handlers (mouse + touch) — attach listeners SYNCHRONOUSLY on
  //    beginDrag so back-to-back mousedown+mouseup events (fast-clicks
  //    from automation or trackpad taps) are always caught. Attaching
  //    via useEffect was flaky because setIsDragging(true) → render →
  //    effect could race the incoming mouseup.

  // Phase 19c/d — track when the mousedown+mouseup drag flow just fired
  // bounce+open so the trailing native `click` handler (real users)
  // no-ops instead of double-firing. In pure programmatic `.click()`
  // (tests, keyboard Enter) the drag flow is skipped, this ref stays
  // false, and the click handler fires bounce+open directly.
  const justHandledMouseUpRef = useRef(false);
  const markMouseUpHandled = useCallback(() => {
    justHandledMouseUpRef.current = true;
    setTimeout(() => {
      justHandledMouseUpRef.current = false;
    }, 500);
  }, []);

  const applyMove = useCallback(
    (clientX, clientY) => {
      const d = dragRef.current;
      if (!d.active) return;
      const dx = clientX - d.startX;
      const dy = clientY - d.startY;
      if (
        Math.abs(dx) > DRAG_THRESHOLD_PX ||
        Math.abs(dy) > DRAG_THRESHOLD_PX
      ) {
        d.moved = true;
      }
      d.pending = clampToViewport(d.origX + dx, d.origY + dy, size);
      if (d.rafId == null) {
        d.rafId = requestAnimationFrame(() => {
          d.rafId = null;
          if (d.pending) setPos(d.pending);
        });
      }
    },
    [size]
  );

  const endDrag = useCallback(() => {
    const d = dragRef.current;
    if (!d.active) return;
    d.active = false;
    setIsDragging(false);
    document.body.style.userSelect = "";
    if (d.rafId) {
      cancelAnimationFrame(d.rafId);
      d.rafId = null;
    }
    // Detach listeners we attached in beginDrag.
    if (globalMoveRef.current) {
      window.removeEventListener("mousemove", globalMoveRef.current);
      window.removeEventListener("touchmove", globalMoveRef.current);
      globalMoveRef.current = null;
    }
    if (globalUpRef.current) {
      window.removeEventListener("mouseup", globalUpRef.current);
      window.removeEventListener("touchend", globalUpRef.current);
      window.removeEventListener("touchcancel", globalUpRef.current);
      globalUpRef.current = null;
    }
    if (d.moved) {
      setPos((p) => {
        persist(p.x, p.y);
        return p;
      });
    } else {
      // Click (no drag) → fire bounce, then open assistant panel after a
      // short delay so the bounce keyframe (420 ms) has a chance to render
      // before the FAB unmounts on open. Suppressed automatically in ASL
      // mode via a11y-asl.css motion sweep (animation-duration 0.01ms).
      fireBounce();
      markMouseUpHandled();
      setTimeout(() => setOpen(true), 220);
    }
  }, [persist, fireBounce, markMouseUpHandled]);

  const beginDrag = useCallback(
    (clientX, clientY) => {
      if (tourActive) return; // drag disabled during tour
      dragRef.current.startX = clientX;
      dragRef.current.startY = clientY;
      dragRef.current.origX = posRef.current.x;
      dragRef.current.origY = posRef.current.y;
      dragRef.current.moved = false;
      dragRef.current.active = true;
      setIsDragging(true);
      document.body.style.userSelect = "none";

      // Attach global listeners SYNCHRONOUSLY here so an immediate mouseup
      // (which happens in fast automation / rapid tap) is always caught.
      const onMove = (e) => {
        if (e.type === "touchmove") {
          if (e.touches.length > 0) {
            applyMove(e.touches[0].clientX, e.touches[0].clientY);
          }
        } else {
          applyMove(e.clientX, e.clientY);
        }
      };
      const onUp = () => endDrag();
      globalMoveRef.current = onMove;
      globalUpRef.current = onUp;
      window.addEventListener("mousemove", onMove);
      window.addEventListener("mouseup", onUp);
      window.addEventListener("touchmove", onMove, { passive: true });
      window.addEventListener("touchend", onUp);
      window.addEventListener("touchcancel", onUp);
    },
    [tourActive, applyMove, endDrag]
  );

  // Cleanup on unmount.
  useEffect(() => {
    return () => {
      if (globalMoveRef.current) {
        window.removeEventListener("mousemove", globalMoveRef.current);
        window.removeEventListener("touchmove", globalMoveRef.current);
      }
      if (globalUpRef.current) {
        window.removeEventListener("mouseup", globalUpRef.current);
        window.removeEventListener("touchend", globalUpRef.current);
        window.removeEventListener("touchcancel", globalUpRef.current);
      }
    };
  }, []);

  // Track when the mousedown+mouseup drag flow just fired bounce+open so
  // the subsequent native `click` handler (real users) no-ops instead of
  // double-firing. In pure programmatic `.click()` (tests, keyboard
  // Enter) the drag flow is skipped, this ref stays false, and the click
  // handler fires bounce+open directly.

  // Hide rules — inside a chat thread OR a modal is open OR the panel is open.
  // NOTE: the FAB stays VISIBLE during the FiguTour (needed for spotlight
  // on step 5 + as a visual anchor while it moves between spotlights).
  const onChatThread = /^\/app\/chat\/[^/]+$/.test(location.pathname);
  const hideFab = onChatThread || (hasOverlay && !tourActive) || open;

  return (
    <>
      {!hideFab && (
        <button
          type="button"
          aria-label="Open Robo-Ball assistant"
          data-testid="roboball-fab"
          data-tour-active={tourActive ? "true" : "false"}
          onMouseDown={(e) => {
            if (tourActive) return;
            e.preventDefault();
            beginDrag(e.clientX, e.clientY);
          }}
          onTouchStart={(e) => {
            if (tourActive) return;
            if (e.touches.length > 0) {
              beginDrag(e.touches[0].clientX, e.touches[0].clientY);
            }
          }}
          onClick={(e) => {
            // Real user mousedown+mouseup already fired the bounce+open via
            // the drag path — swallow the trailing native click so we don't
            // double-fire. Otherwise (programmatic `.click()`, keyboard
            // Enter, tests) we mirror the mouseup behaviour.
            e.preventDefault();
            if (tourActive) return;
            if (justHandledMouseUpRef.current) return;
            if (open) return;
            fireBounce();
            setTimeout(() => setOpen(true), 220);
          }}
          className="figumatch-fab"
          style={{
            position: "fixed",
            left: `${pos.x}px`,
            top: `${pos.y}px`,
            width: `${size}px`,
            height: `${size}px`,
            zIndex: 9999,
            cursor: tourActive ? "default" : isDragging ? "grabbing" : "grab",
            touchAction: "none",
            transition: smoothTransition ? TOUR_TRANSITION : undefined,
          }}
        >
          <div
            data-testid="roboball-fab-motion"
            className={`figumatch-fab-motion ${motion}`.trim()}
            style={{ position: "relative", width: "100%", height: "100%" }}
          >
            <RoboBallImage
              className="figumatch-fab-img"
              fallback={
                <span className="figumatch-fab-fallback" aria-hidden="true">
                  <span className="figumatch-fab-led figumatch-fab-led--l" />
                  <span className="figumatch-fab-led figumatch-fab-led--r" />
                </span>
              }
            />
            <EyeExpression />
          </div>
        </button>
      )}
      <RoboBallGreetingBubble
        fabPos={pos}
        fabSize={size}
        hidden={
          hideFab ||
          open ||
          tourActive ||
          hasOverlay ||
          location.pathname === "/app/admin"
        }
        assistantOpen={open}
        isAsl={isAsl}
        onRoll={fireRoll}
        greetingText={t("roboball.periodic_greeting")}
        dismissLabel={t("roboball.greeting_dismiss", "Dismiss")}
      />
      <AssistantPanel open={open} onOpenChange={setOpen} />
    </>
  );
}

/**
 * Phase 19c/d — 60 s periodic greeting bubble.
 *
 * In non-ASL mode:
 *   • Every 60 s (staggered 8 s after mount to avoid clashing with the
 *     auto-tour first-login flow) show the bubble for 4 s + fire roboRoll.
 *   • User can dismiss with the ×.
 *   • Any of the `hidden` reasons suppresses the bubble for that cycle.
 *
 * In ASL mode:
 *   • No periodic cycle. Bubble shows exactly once per tab session, on
 *     the FIRST time the assistant chat panel transitions closed → open
 *     → closed. Same translated text, same × dismiss, no fade timer.
 */
function RoboBallGreetingBubble({
  fabPos,
  fabSize,
  hidden,
  assistantOpen,
  isAsl,
  onRoll,
  greetingText,
  dismissLabel,
}) {
  const [visible, setVisible] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const timersRef = useRef({ show: null, hide: null });
  const dismissedRef = useRef(false);
  // ASL-only: track whether we've already shown the one-shot badge.
  const aslShownRef = useRef(false);
  const prevAssistantOpenRef = useRef(assistantOpen);

  // Cleanup on unmount.
  useEffect(() => {
    return () => {
      if (timersRef.current.show) clearTimeout(timersRef.current.show);
      if (timersRef.current.hide) clearTimeout(timersRef.current.hide);
    };
  }, []);

  const showBubble = useCallback(
    ({ autoHide }) => {
      if (dismissedRef.current) return;
      if (hidden) return;
      setLeaving(false);
      setVisible(true);
      if (!isAsl) onRoll();
      if (autoHide) {
        if (timersRef.current.hide) clearTimeout(timersRef.current.hide);
        timersRef.current.hide = setTimeout(() => {
          setLeaving(true);
          setTimeout(() => {
            setVisible(false);
            setLeaving(false);
          }, 320);
        }, 4000);
      }
    },
    [hidden, isAsl, onRoll]
  );

  // Non-ASL: 60 s interval (first fire after 8 s).
  useEffect(() => {
    if (isAsl) return undefined;
    let interval = null;
    timersRef.current.show = setTimeout(() => {
      showBubble({ autoHide: true });
      interval = setInterval(() => showBubble({ autoHide: true }), 60_000);
    }, 8000);
    return () => {
      if (timersRef.current.show) clearTimeout(timersRef.current.show);
      if (interval) clearInterval(interval);
    };
  }, [isAsl, showBubble]);

  // Phase 19c/d — expose a test hook so external verifiers can trigger
  // the greeting bubble without waiting for the 8 s / 60 s interval.
  // Idempotent — repeated calls just re-show the bubble.
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    window.__figumatch_test_hooks = window.__figumatch_test_hooks || {};
    window.__figumatch_test_hooks.showGreetingBubble = () =>
      showBubble({ autoHide: false });
    return () => {
      if (window.__figumatch_test_hooks) {
        delete window.__figumatch_test_hooks.showGreetingBubble;
      }
    };
  }, [showBubble]);

  // ASL: one-shot after the first assistant close transition.
  useEffect(() => {
    if (!isAsl) return;
    const wasOpen = prevAssistantOpenRef.current;
    prevAssistantOpenRef.current = assistantOpen;
    if (wasOpen && !assistantOpen && !aslShownRef.current) {
      aslShownRef.current = true;
      showBubble({ autoHide: false });
    }
  }, [assistantOpen, isAsl, showBubble]);

  // Hide + cancel timers when a suppress condition kicks in mid-window.
  useEffect(() => {
    if (hidden && visible) {
      setVisible(false);
      setLeaving(false);
      if (timersRef.current.hide) {
        clearTimeout(timersRef.current.hide);
        timersRef.current.hide = null;
      }
    }
  }, [hidden, visible]);

  if (!visible) return null;

  // Position the bubble left of the FAB (or above if the FAB is far-left).
  const vw = typeof window !== "undefined" ? window.innerWidth : 1280;
  const bubbleWidth = 260;
  const gap = 12;
  const leftOfFab = fabPos.x - bubbleWidth - gap;
  const canPlaceLeft = leftOfFab >= 8;
  const top =
    fabPos.y + fabSize / 2 - 24; // vertically-centre relative to the ball
  const left = canPlaceLeft
    ? leftOfFab
    : Math.max(8, Math.min(vw - bubbleWidth - 8, fabPos.x));
  const positionStyle = canPlaceLeft
    ? { left, top }
    : { left, top: Math.max(8, fabPos.y - 80) };

  const onDismiss = () => {
    dismissedRef.current = true;
    setLeaving(true);
    setTimeout(() => {
      setVisible(false);
      setLeaving(false);
    }, 320);
  };

  return (
    <div
      data-testid="roboball-greeting-bubble"
      data-mode={isAsl ? "asl-static" : "periodic"}
      className={`roboball-greeting-bubble${leaving ? " leaving" : ""}`}
      style={{ position: "fixed", ...positionStyle, width: bubbleWidth }}
    >
      <button
        type="button"
        data-testid="roboball-greeting-dismiss"
        aria-label={dismissLabel}
        onClick={onDismiss}
        className="roboball-greeting-bubble__close"
      >
        ×
      </button>
      <span data-testid="roboball-greeting-text">{greetingText}</span>
    </div>
  );
}
